/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * FikoCAD Diff Worker Pool - Multi-Core CPU Paralel İşleme
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * GPU mevcut değilse, tüm CPU core'larını kullanarak paralel mesafe hesaplaması yapar.
 * Transferable objects ile zero-copy veri aktarımı sağlar.
 *
 * Performans:
 * - 4 worker (4 core): ~4x speedup
 * - 8 worker (8 core): ~6-7x speedup (diminishing returns)
 *
 * @author FikoYT
 */

export interface WorkerPoolResult {
  distances: Float32Array;
  cpuTimeMs: number;
  workersUsed: number;
}

interface PendingTask {
  resolve: (result: Float32Array) => void;
  reject: (error: Error) => void;
}

export class FikoCadDiffWorkerPool {
  private workers: Worker[] = [];
  private workerCount: number;
  private initialized = false;
  private pendingTasks: Map<number, PendingTask> = new Map();
  private taskIdCounter = 0;

  // Worker yolu (bundle edilmiş)
  private readonly WORKER_PATH = './cad-diff.worker.ts';

  constructor(workerCount?: number) {
    // Core sayısını al, max 8 worker
    this.workerCount = workerCount ?? Math.min(navigator.hardwareConcurrency || 4, 8);
  }

  /**
   * Worker pool'u başlat
   */
  async initialize(): Promise<boolean> {
    if (this.initialized) return true;

    try {
      // Worker'ları oluştur
      const workerPromises: Promise<Worker>[] = [];

      for (let i = 0; i < this.workerCount; i++) {
        workerPromises.push(this.createWorker(i));
      }

      this.workers = await Promise.all(workerPromises);
      this.initialized = true;

      console.log(`[FikoCadDiffWorkerPool] ${this.workerCount} worker başlatıldı`);
      return true;

    } catch (err) {
      console.error('[FikoCadDiffWorkerPool] Başlatma hatası:', err);
      return false;
    }
  }

  private createWorker(id: number): Promise<Worker> {
    return new Promise((resolve, reject) => {
      try {
        // Inline worker oluştur (bundler sorunlarını önlemek için)
        const workerCode = this.getWorkerCode();
        const blob = new Blob([workerCode], { type: 'application/javascript' });
        const worker = new Worker(URL.createObjectURL(blob));

        worker.onmessage = (e) => {
          const msg = e.data;

          if (msg.type === 'pong' && msg.id === -1) {
            // Worker hazır
            worker.onmessage = (e) => this.handleWorkerMessage(e, id);
            resolve(worker);
            return;
          }

          this.handleWorkerMessage(e, id);
        };

        worker.onerror = (err) => {
          console.error(`[WorkerPool] Worker ${id} hatası:`, err);
          reject(err);
        };

        // Ping ile kontrol et
        setTimeout(() => {
          worker.postMessage({ type: 'ping', id: 0 });
        }, 100);

      } catch (err) {
        reject(err);
      }
    });
  }

  private handleWorkerMessage(e: MessageEvent, workerId: number): void {
    const msg = e.data;

    if (msg.type === 'result' && this.pendingTasks.has(msg.id)) {
      const task = this.pendingTasks.get(msg.id)!;
      this.pendingTasks.delete(msg.id);

      if (msg.error) {
        task.reject(new Error(msg.error));
      } else {
        task.resolve(msg.distances);
      }
    }
  }

  /**
   * Paralel mesafe hesapla
   *
   * @param queryPoints Query noktaları (Float32Array - vec4 format)
   * @param targetPoints Target noktaları (Float32Array - vec4 format)
   * @param cellSize Spatial hash cell size
   */
  async computeDistances(
    queryPoints: Float32Array,
    targetPoints: Float32Array,
    cellSize: number
  ): Promise<WorkerPoolResult | null> {
    if (!this.initialized || this.workers.length === 0) {
      console.warn('[FikoCadDiffWorkerPool] Pool hazır değil');
      return null;
    }

    const queryCount = queryPoints.length / 4;
    const startTime = performance.now();

    console.log(`[FikoCadDiffWorkerPool] Paralel hesaplama başlıyor: ${queryCount} query, ${this.workerCount} worker`);

    try {
      // Her worker'a eşit iş dağıt
      const chunkSize = Math.ceil(queryCount / this.workerCount);
      const promises: Promise<Float32Array>[] = [];

      for (let i = 0; i < this.workerCount; i++) {
        const startIndex = i * chunkSize;
        const count = Math.min(chunkSize, queryCount - startIndex);

        if (count <= 0) break;

        const taskId = this.taskIdCounter++;
        const promise = new Promise<Float32Array>((resolve, reject) => {
          this.pendingTasks.set(taskId, { resolve, reject });

          // Worker'a mesaj gönder
          // NOT: targetPoints'i her worker'a kopyalamamak için
          // tüm veriyi gönderiyoruz, worker kendi aralığını işleyecek
          this.workers[i].postMessage({
            type: 'compute',
            id: taskId,
            data: {
              queryPoints,
              targetPoints,
              startIndex,
              count,
              cellSize
            }
          });
        });

        promises.push(promise);
      }

      // Tüm worker'ların tamamlanmasını bekle
      const results = await Promise.all(promises);

      // Sonuçları birleştir
      const allDistances = new Float32Array(queryCount);
      let offset = 0;

      for (const chunk of results) {
        allDistances.set(chunk, offset);
        offset += chunk.length;
      }

      const cpuTimeMs = performance.now() - startTime;
      console.log(`[FikoCadDiffWorkerPool] Tamamlandı: ${cpuTimeMs.toFixed(1)}ms`);

      return {
        distances: allDistances,
        cpuTimeMs,
        workersUsed: Math.min(this.workerCount, Math.ceil(queryCount / chunkSize))
      };

    } catch (err) {
      console.error('[FikoCadDiffWorkerPool] Hesaplama hatası:', err);
      return null;
    }
  }

  /**
   * Pool kullanılabilir mi?
   */
  isAvailable(): boolean {
    return this.initialized && this.workers.length > 0;
  }

  /**
   * Worker sayısını al
   */
  getWorkerCount(): number {
    return this.workerCount;
  }

  /**
   * Kaynakları temizle
   */
  dispose(): void {
    for (const worker of this.workers) {
      worker.terminate();
    }
    this.workers = [];
    this.pendingTasks.clear();
    this.initialized = false;
  }

  /**
   * Inline worker kodu (bundler sorunlarını önlemek için)
   */
  private getWorkerCode(): string {
    return `
/**
 * CAD Diff WebWorker - Inline Version
 */

class WorkerSpatialHash {
  constructor(cellSize) {
    this.cellSize = cellSize;
    this.invCellSize = 1.0 / cellSize;
    this.cells = new Map();
    this.cellCounts = new Map();
  }

  hashKey(x, y, z) {
    const ix = Math.floor(x * this.invCellSize);
    const iy = Math.floor(y * this.invCellSize);
    const iz = Math.floor(z * this.invCellSize);
    return ((ix * 73856093) ^ (iy * 19349663) ^ (iz * 83492791)) >>> 0;
  }

  build(points) {
    const pointCount = points.length / 4;

    // İlk geçiş: cell sayılarını hesapla
    for (let i = 0; i < pointCount; i++) {
      const idx = i * 4;
      const hash = this.hashKey(points[idx], points[idx + 1], points[idx + 2]);
      this.cellCounts.set(hash, (this.cellCounts.get(hash) || 0) + 1);
    }

    // Cell array'leri oluştur
    for (const [hash, count] of this.cellCounts) {
      this.cells.set(hash, new Float32Array(count * 3));
      this.cellCounts.set(hash, 0);
    }

    // İkinci geçiş: noktaları yerleştir
    for (let i = 0; i < pointCount; i++) {
      const idx = i * 4;
      const x = points[idx];
      const y = points[idx + 1];
      const z = points[idx + 2];
      const hash = this.hashKey(x, y, z);
      const cellIdx = this.cellCounts.get(hash) * 3;
      const cell = this.cells.get(hash);
      cell[cellIdx] = x;
      cell[cellIdx + 1] = y;
      cell[cellIdx + 2] = z;
      this.cellCounts.set(hash, this.cellCounts.get(hash) + 1);
    }
  }

  hashKeyFromInts(ix, iy, iz) {
    return ((ix * 73856093) ^ (iy * 19349663) ^ (iz * 83492791)) >>> 0;
  }

  findNearest(x, y, z, maxDist) {
    const baseIx = Math.floor(x * this.invCellSize);
    const baseIy = Math.floor(y * this.invCellSize);
    const baseIz = Math.floor(z * this.invCellSize);

    let minDistSq = maxDist * maxDist;
    const searchRadius = 2;

    for (let dx = -searchRadius; dx <= searchRadius; dx++) {
      for (let dy = -searchRadius; dy <= searchRadius; dy++) {
        for (let dz = -searchRadius; dz <= searchRadius; dz++) {
          const hash = this.hashKeyFromInts(baseIx + dx, baseIy + dy, baseIz + dz);
          const cell = this.cells.get(hash);

          if (cell) {
            for (let i = 0; i < cell.length; i += 3) {
              const px = cell[i];
              const py = cell[i + 1];
              const pz = cell[i + 2];
              const dx2 = x - px;
              const dy2 = y - py;
              const dz2 = z - pz;
              const distSq = dx2 * dx2 + dy2 * dy2 + dz2 * dz2;
              if (distSq < minDistSq) {
                minDistSq = distSq;
              }
            }
          }
        }
      }
    }

    return Math.sqrt(minDistSq);
  }
}

self.onmessage = function(e) {
  const msg = e.data;

  switch (msg.type) {
    case 'ping':
      self.postMessage({ type: 'pong', id: msg.id });
      break;

    case 'compute':
      handleCompute(msg);
      break;
  }
};

function handleCompute(msg) {
  const data = msg.data;
  const start = performance.now();

  try {
    const localHash = new WorkerSpatialHash(data.cellSize);
    localHash.build(data.targetPoints);

    const distances = new Float32Array(data.count);
    const maxDist = 1e10;

    for (let i = 0; i < data.count; i++) {
      const globalIdx = (data.startIndex + i) * 4;
      const x = data.queryPoints[globalIdx];
      const y = data.queryPoints[globalIdx + 1];
      const z = data.queryPoints[globalIdx + 2];
      distances[i] = localHash.findNearest(x, y, z, maxDist);
    }

    const result = {
      type: 'result',
      id: msg.id,
      distances: distances,
      timeMs: performance.now() - start
    };

    self.postMessage(result, [distances.buffer]);
  } catch (err) {
    self.postMessage({
      type: 'result',
      id: msg.id,
      error: err.message
    });
  }
}

// Worker hazır
self.postMessage({ type: 'pong', id: -1, timeMs: 0 });
`;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// SINGLETON
// ═══════════════════════════════════════════════════════════════════════════════

let _poolInstance: FikoCadDiffWorkerPool | null = null;

export function getCadDiffWorkerPool(): FikoCadDiffWorkerPool {
  if (!_poolInstance) {
    _poolInstance = new FikoCadDiffWorkerPool();
  }
  return _poolInstance;
}
